import * as React from "react";
import { Group } from "../../application/domain/group/group";
import { useEffect, useState } from "react";
import apiClient from "../../application/apis/tenant-management/tenant-management-client";
import { toast } from "react-toastify";
import { components, GroupBase, MenuListProps } from "react-select";
import { useDebounce } from "use-debounce";
import { reportUnexpectedError } from "../../error-reporter";
import { ReactSelect, ReactSelectProps } from "@graffer-inc/console-design-kit";
import { useAuthContextV2 } from "../../auth-state";

type OptionType = {
  label: string;
  value: string;
};

const MAX_CANDIDATE_GROUP_LIMIT = 50;

export const GroupShareSelectField: React.FC<{
  selectedGroupId: string;
  tenantId?: string; // Service-Providerの場合のみ指定
  onChange: (groupId: string) => void;
  description?: string;
}> = ({
  tenantId,
  selectedGroupId,
  onChange,
  description = "グループに所属するユーザーのみ閲覧や編集が可能になります。",
}) => {
  const { auth } = useAuthContextV2();
  const [name, setName] = useState<string>("");
  const [bouncedName] = useDebounce(name, 500);
  const [selectedTenantId, _] = useState(
    auth.hasRole("GrafferStaff") ? tenantId : undefined,
  );
  const [currentGroup, setCurrentGroup] = useState<Group>();
  const [candidateGroups, setCandidateGroups] = useState<Map<string, Group>>(
    new Map<string, Group>(),
  );
  const fetchCurrentGroup = React.useCallback(async () => {
    if (!selectedGroupId || selectedGroupId === "") {
      return;
    }
    try {
      const { data: group } = await apiClient.fetchGroupById(selectedGroupId);
      setCurrentGroup(group);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("グループの取得に失敗しました。" + e.message);
    }
  }, [selectedGroupId, setCurrentGroup]);

  const [page, setPage] = useState<number>(1);

  const isDisplayBackButton = React.useMemo(() => {
    return page !== 1;
  }, [page]);
  const isDisplayNextButton = React.useMemo(() => {
    // ページングの上限が50件だが、処理内で現在設定しているグループを候補リストに設定している。
    // そのため、ページング条件以上で判定している。
    return candidateGroups.size >= MAX_CANDIDATE_GROUP_LIMIT;
  }, [candidateGroups]);

  const fetchCandidateGroups = React.useCallback(async () => {
    const groupsMap = new Map<string, Group>();
    try {
      if (selectedTenantId) {
        const { data: groups } = await apiClient.fetchGroupsByOrganizationId(
          selectedTenantId,
          page,
          MAX_CANDIDATE_GROUP_LIMIT,
          {
            name: bouncedName,
          },
        );
        groups.forEach((g) => groupsMap.set(g.id!, g));
      } else {
        const { data: groupMetadata } = await apiClient.fetchGroupsMetadata(
          page,
          MAX_CANDIDATE_GROUP_LIMIT,
          {
            ids: auth.groupIDs,
            name: bouncedName,
          },
        );
        groupMetadata.forEach((g) =>
          groupsMap.set(g.id!, {
            id: g.id,
            name: g.name,
            email: "",
            isIgnoreAclSetting: false,
          } as Group),
        );
      }
      setCandidateGroups(groupsMap);
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("グループ一覧の取得に失敗しました。");
    }
  }, [selectedTenantId, bouncedName, setCandidateGroups, page]);

  useEffect(() => {
    fetchCurrentGroup();
    fetchCandidateGroups();
  }, [fetchCurrentGroup, fetchCandidateGroups]);

  const candidates: {
    label: string;
    value?: string;
  }[] = [];
  if (currentGroup) {
    candidateGroups.set(currentGroup.id!, currentGroup);
  }
  candidateGroups.forEach((g) =>
    candidates.push({
      label: g.name,
      value: g.id,
    }),
  );

  return (
    <ReactSelect
      label="管理グループ"
      description={description}
      placeholder={
        candidates.length === 0
          ? "グループ読込中..."
          : "選択またはキーワードで検索"
      }
      value={
        candidates.find(
          (g) => g.value === selectedGroupId,
        ) as ReactSelectProps<string>["value"]
      }
      onInputChange={(input) => {
        setName(input);
        setPage(1);
      }}
      components={
        {
          MenuList: MenuListBuilder({
            isDisplayBackButton: isDisplayBackButton,
            isDisplayNextButton: isDisplayNextButton,
            backPage: () => setPage(page - 1),
            nextPage: () => setPage(page + 1),
          }),
        } as ReactSelectProps<string>["components"]
      }
      onChange={(selectedOption) => {
        if (selectedOption && (selectedOption as OptionType).value) {
          onChange((selectedOption as OptionType).value);
        }
      }}
      options={candidates as ReactSelectProps<string>["options"]}
      required
    />
  );
};

type GroupMenuListProps = MenuListProps<
  {
    label: string;
    value?: string | undefined;
  },
  false,
  GroupBase<{
    label: string;
    value?: string | undefined;
  }>
>;

const MenuListBuilder = (arg: {
  isDisplayBackButton: boolean;
  isDisplayNextButton: boolean;
  backPage: () => void;
  nextPage: () => void;
}): React.ComponentType<GroupMenuListProps> | undefined => {
  const GroupMenuList: React.ComponentType<GroupMenuListProps> | undefined = (
    props,
  ) => {
    return (
      <>
        <components.MenuList {...props}>
          {props.children}
          {(arg.isDisplayBackButton || arg.isDisplayNextButton) && (
            <div className="m-4 is-flex is-justify-content-center">
              {arg.isDisplayBackButton && (
                <button
                  className="button is-size-7 mr-3"
                  onClick={arg.backPage}
                >
                  <span className="icon has-text-dark">
                    <i className="fas fa-chevron-left"></i>
                  </span>
                </button>
              )}
              {arg.isDisplayNextButton && (
                <button className="button is-size-7" onClick={arg.nextPage}>
                  <span className="icon has-text-dark">
                    <i className="fas fa-chevron-right"></i>
                  </span>
                </button>
              )}
            </div>
          )}
        </components.MenuList>
      </>
    );
  };

  return GroupMenuList;
};
